/**
 * Withdraw Modal Component
 *
 * Confirmation modal for withdrawing from a cohort.
 */

import { useState } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';

interface WithdrawModalProps {
  visible: boolean;
  cohortName: string;
  onDismiss: () => void;
  onConfirm: () => Promise<void>;
}

export function WithdrawModal({
  visible,
  cohortName,
  onDismiss,
  onConfirm,
}: WithdrawModalProps) {
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setIsWithdrawing(true);
    setError(null);
    try {
      await onConfirm();
      onDismiss();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to withdraw from cohort');
    } finally {
      setIsWithdrawing(false);
    }
  };

  const handleDismiss = () => {
    if (!isWithdrawing) {
      setError(null);
      onDismiss();
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      header="Withdraw from cohort"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDismiss} disabled={isWithdrawing}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirm}
              loading={isWithdrawing}
            >
              Withdraw
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Box>
          Are you sure you want to withdraw from <strong>{cohortName}</strong>?
          Your progress will be preserved, but you will need to contact a program admin to re-enroll.
        </Box>
      </SpaceBetween>
    </Modal>
  );
}
